
import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { 
  TrendingUp, 
  TrendingDown,
  Shield,
  ShieldAlert,
  Globe
} from "lucide-react";

const LatAmPage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-noa-rich-black py-6 px-4 md:px-8">
        <div className="container mx-auto">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center">
            LatAm - Emerging Economy Opportunities and Risks
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Introduction Card */}
          <Card className="col-span-1 md:col-span-2 shadow-md border-blue-100">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <Globe className="h-8 w-8 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-noa-rich-black">Latin America: A Region of Contrasts</h2>
              </div>
              <p className="text-noa-paynes-gray mb-4">
                Latin America represents one of the world's most dynamic emerging markets, with a population of over 650 million and a combined GDP of approximately $5.5 trillion. The region's unique blend of natural resources, demographic advantages, and ongoing digital transformation creates distinctive opportunities alongside significant challenges.
              </p>
              <p className="text-noa-paynes-gray">
                Understanding both the potential upside and inherent risks is essential for investors and businesses looking to participate in Latin America's growth story.
              </p>
            </CardContent>
          </Card>

          {/* Opportunities Section */}
          <Card className="shadow-md border-green-100">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <TrendingUp className="h-6 w-6 text-green-600 mr-3" />
                <h2 className="text-xl font-bold text-noa-rich-black">Key Opportunities</h2>
              </div>
              <ul className="space-y-4">
                <li>
                  <h3 className="font-semibold text-green-700">Demographic Dividend</h3>
                  <p className="text-sm text-noa-paynes-gray">LatAm's working-age population continues to grow while developed markets and China face aging demographics, providing a sustained labor and consumption advantage.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-green-700">Digital Transformation</h3>
                  <p className="text-sm text-noa-paynes-gray">Rapid adoption of digital technologies, accelerated by the pandemic, has created fertile ground for SaaS and fintech solutions across previously underserved industries.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-green-700">Nearshoring Boom</h3>
                  <p className="text-sm text-noa-paynes-gray">Companies are increasingly relocating supply chains to Latin America to reduce geopolitical risk and logistics costs, creating significant opportunity for B2B service providers.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-green-700">Valuation Arbitrage</h3>
                  <p className="text-sm text-noa-paynes-gray">Assets typically trade at 30-50% discounts to comparable businesses in developed markets, creating attractive entry points for strategic acquirers.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-green-700">Middle Class Expansion</h3>
                  <p className="text-sm text-noa-paynes-gray">Projected growth to 345M middle-class consumers by 2030, driving increased consumption and software adoption across both B2C and B2B sectors.</p>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Risks Section */}
          <Card className="shadow-md border-red-100">
            <CardContent className="p-6">
              <div className="flex items-center mb-4">
                <ShieldAlert className="h-6 w-6 text-red-500 mr-3" />
                <h2 className="text-xl font-bold text-noa-rich-black">Key Risks</h2>
              </div>
              <ul className="space-y-4">
                <li>
                  <h3 className="font-semibold text-red-600">Currency Volatility</h3>
                  <p className="text-sm text-noa-paynes-gray">Local currencies can experience significant fluctuations, complicating financial planning and potentially eroding USD returns without proper hedging strategies.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-red-600">Regulatory Complexity</h3>
                  <p className="text-sm text-noa-paynes-gray">Significant variance in tax and labor regulations across countries creates compliance challenges, requiring local expertise to navigate effectively.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-red-600">Political Uncertainty</h3>
                  <p className="text-sm text-noa-paynes-gray">Electoral cycles can bring policy shifts, though the impact on private businesses has moderated as most major economies maintain market-friendly frameworks regardless of leadership.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-red-600">Inflationary Pressures</h3>
                  <p className="text-sm text-noa-paynes-gray">Many economies face persistent inflation challenges, requiring businesses to implement dynamic pricing strategies and efficient cost control mechanisms.</p>
                </li>
                <li>
                  <h3 className="font-semibold text-red-600">Infrastructure Gaps</h3>
                  <p className="text-sm text-noa-paynes-gray">Digital and physical infrastructure varies significantly across and within countries, potentially limiting scalability in certain regions and market segments.</p>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Risk Mitigation Strategy */}
        <Card className="shadow-md border-blue-100 mb-12">
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-bold text-noa-rich-black">Our Risk Mitigation Approach</h2>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                <p className="text-noa-paynes-gray"><span className="font-medium">Local expertise</span> – Our investment team combines deep local market knowledge with global best practices.</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                <p className="text-noa-paynes-gray"><span className="font-medium">Diversification</span> – We build exposure across multiple countries, verticals and customer segments.</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                <p className="text-noa-paynes-gray"><span className="font-medium">Focus on resilient sectors</span> – We target mission-critical software with sticky customer relationships and proven recession resistance.</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                <p className="text-noa-paynes-gray"><span className="font-medium">Conservative capital structure</span> – We maintain low leverage and prioritize sustainable free cash flow generation.</p>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 font-bold mr-2 mt-1">•</span>
                <p className="text-noa-paynes-gray"><span className="font-medium">Natural hedges</span> – We seek businesses with USD-linked revenues or the ability to adjust pricing to inflation.</p>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="shadow-md border-blue-100">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-noa-rich-black mb-4">Why This Matters</h2>
            <p className="text-noa-paynes-gray mb-4">
              The combination of favorable demographics, digital transformation, and valuation arbitrage creates a compelling investment case for Latin American software businesses, particularly those with mission-critical applications and resilient business models.
            </p>
            <p className="text-noa-paynes-gray">
              By applying our experienced local lens and disciplined acquisition approach, we can navigate the region's complexities while capitalizing on its significant growth potential to deliver attractive risk-adjusted returns.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LatAmPage;
